import { useMemo } from "react"
import { ColumnDef } from "@tanstack/react-table"
import DataTable from "Components/molecule/data-table"
import useDataTable from "hooks/useDataTable"
import { StageSummary } from "models/opportunity-dashboard"
import { money } from "./format"

interface StageSummaryTableProps {
    rows: StageSummary[]
    // Column title of the stage column ("Stage", "Description", …)
    stageHeader?: string
    // When set, stage names are clickable and filter the opportunity list below
    selectedStage?: string | null
    onSelectStage?: (stage: string | null) => void
    // Stage to highlight (e.g. the outcome of a report)
    highlightStage?: string
}

const footerClass = () => "financial-cell-footer"
const footerClassEnd = () => "financial-cell-footer text-end"

// Value and count per stage, with a total row.
const StageSummaryTable = ({ rows, stageHeader = "Stage", selectedStage, onSelectStage, highlightStage }: StageSummaryTableProps) => {
    const totals = useMemo(() => rows.reduce(
        (acc, r) => ({ value: acc.value + r.value, count: acc.count + r.count }),
        { value: 0, count: 0 },
    ), [rows])

    const columns = useMemo<ColumnDef<StageSummary>[]>(() => [
        {
            accessorKey: "stage",
            header: stageHeader,
            size: 260,
            cell: ({ row }) => {
                const stage = row.original.stage
                const isSelected = selectedStage === stage
                const isHighlighted = highlightStage === stage
                if (!onSelectStage)
                    return <span className={isHighlighted ? "fw-semibold" : undefined}>{stage}</span>
                return <button
                    type="button"
                    className={`btn btn-link p-0 text-start ${isSelected ? "fw-bold" : ""}`}
                    title={isSelected ? "Show all stages" : `Show only ${stage}`}
                    onClick={() => onSelectStage(isSelected ? null : stage)}
                >
                    {stage}{isSelected && <i className="mdi mdi-filter ms-1" />}
                </button>
            },
            footer: () => "Total",
            meta: { getFooterClassName: footerClass },
        },
        {
            accessorKey: "value",
            header: "Value",
            size: 160,
            cell: ({ getValue }) => money(getValue<number>()),
            footer: () => money(totals.value),
            meta: {
                getHeaderClassName: () => "text-end",
                cellClassName: "text-end",
                getFooterClassName: footerClassEnd,
            },
        },
        {
            accessorKey: "count",
            header: "Count",
            size: 100,
            footer: () => totals.count,
            meta: {
                getHeaderClassName: () => "text-end",
                cellClassName: "text-end",
                getFooterClassName: footerClassEnd,
            },
        },
    ], [stageHeader, selectedStage, onSelectStage, highlightStage, totals])

    const table = useDataTable({ data: rows, columns })

    return <DataTable table={table} columns={columns} />
}

export default StageSummaryTable
